import { Booking } from "../../../models/booking";
import { CartonView, CartonProductView, Carton } from "../../../models/carton";
import { PurchaseOrder } from "../../../models/purchase.order";
import { CommercialInvoice } from "../../../models/shipments";
import { Location } from "../../../models/location";
import { BookingStatus } from "../../../util/enums";
import { DecimalFormatType, formatDecimalValue } from "../../../util/formatDecimalValue";

export interface CommercialInvoiceSSCCGateOptions {
  commercialInvoice?: CommercialInvoice;
  isLoadingPortFromBangladesh?: boolean;
  isExporterInfoDifferentFromVendor?: boolean;
}

const hasRequiredString = (value?: string | null) => !!value?.trim()?.length;

/** Mirrors Commercial Invoice Yup rules in Booking Maintenance (excludes marks/containers). */
export const isCommercialInvoiceCompleteForSSCC = ({
  commercialInvoice,
  isLoadingPortFromBangladesh = false,
  isExporterInfoDifferentFromVendor = false,
}: CommercialInvoiceSSCCGateOptions): boolean => {
  if (!commercialInvoice) return false;

  const baseFieldsComplete =
    hasRequiredString(commercialInvoice.beneficiaryName) &&
    hasRequiredAddress(commercialInvoice.beneficiaryAddress) &&
    hasRequiredString(commercialInvoice.fiscalAgentDetails) &&
    hasRequiredString(commercialInvoice.salesContractNumber) &&
    hasRequiredString(commercialInvoice.salesContractDate) &&
    hasRequiredString(commercialInvoice.expNumber) &&
    hasRequiredString(commercialInvoice.expDate) &&
    hasRequiredString(commercialInvoice.paymentTerms) &&
    !!commercialInvoice.countryId &&
    hasRequiredString(commercialInvoice.vendorId);

  if (!baseFieldsComplete) return false;

  if (isExporterInfoDifferentFromVendor) {
    if (
      !hasRequiredString(commercialInvoice.exporterName) ||
      !hasRequiredString(commercialInvoice.exporterContactDetails)
    ) {
      return false;
    }
  }

  if (isLoadingPortFromBangladesh) {
    return (
      hasRequiredString(commercialInvoice.bankName) &&
      hasRequiredAddress(commercialInvoice.bankAddress) &&
      hasRequiredString(commercialInvoice.accountNumber) &&
      hasRequiredString(commercialInvoice.swiftCode) &&
      hasRequiredString(commercialInvoice.termsOfDelivery) &&
      hasRequiredString(commercialInvoice.udNumber) &&
      hasRequiredString(commercialInvoice.ercNumber) &&
      hasRequiredString(commercialInvoice.rexNumber)
    );
  }

  return true;
};

export interface SSCCPrerequisiteOptions {
  booking?: Booking;
  commercialInvoice?: CommercialInvoice;
  isLoadingPortFromBangladesh?: boolean;
  isExporterInfoDifferentFromVendor?: boolean;
}

const hasRequiredAddress = (address?: Location | null) => {
  if (!address || typeof address !== "object" || Array.isArray(address)) return false;

  return !!(
    address.addressLine1?.trim() ||
    address.longAddress?.trim() ||
    address.suburb?.trim() ||
    address.country?.trim()
  );
};

/** Translation keys for missing prerequisites before Create SSCC / Upload CSV. */
export const getSSCCPrerequisiteTranslationKeys = ({
  booking,
  commercialInvoice,
  isLoadingPortFromBangladesh = false,
  isExporterInfoDifferentFromVendor = false,
}: SSCCPrerequisiteOptions): string[] => {
  const messages: string[] = [];

  if (!booking?.shipmentType) {
    messages.push("youMustSelectABookingModeFirst");
  }
  if (!booking?.portOfLoadingId) {
    messages.push("youMustSelectABookingLoadingPortFirst");
  }
  if (!booking?.portOfDischargeId) {
    messages.push("youMustSelectABookingDischargePortFirst");
  }
  if (!booking?.cargoReadyDate) {
    messages.push("youMustSelectedABookingCargoReadyDateFirst");
  }

  const isCommercialInvoiceComplete = isCommercialInvoiceCompleteForSSCC({
    commercialInvoice: commercialInvoice ?? booking?.commercialInvoice,
    isLoadingPortFromBangladesh,
    isExporterInfoDifferentFromVendor,
  });

  if (!isCommercialInvoiceComplete) {
    messages.push("completeCommercialInvoiceBeforeCreateSSCC");
  }

  return messages;
};

export const convertCartonProductsToCartonsView = (booking?: Booking) => {
  if (!booking) return [];

  const { cartons } = booking;

  const cartonsView: CartonView[] = [];

  if (cartons && !!cartons.length) {
    for (const carton of cartons) {
      if (!carton.sscc) continue;

      const { cartonProducts } = carton;

      if (!cartonsView.find((cartonView) => cartonView.id === carton.id)) cartonsView.push(carton);

      if (cartonProducts && !!cartonProducts.length) {
        const products: CartonProductView[] = [];

        for (const cartonProduct of cartonProducts) {
          if (!cartonProduct) continue;

          products.push({
            ...cartonProduct.product,
            colorDescription:
              cartonProduct?.purchaseOrderProduct?.colorDescription ||
              cartonProduct?.product?.colorDescription,
            itemDescription:
              cartonProduct?.purchaseOrderProduct?.itemDescription ||
              cartonProduct?.product?.itemDescription,
            description:
              cartonProduct?.purchaseOrderProduct?.description ||
              cartonProduct?.product?.description,
            deletedAt: cartonProduct.purchaseOrderProduct?.deletedAt,
            construction: cartonProduct.construction,
            lining: cartonProduct.lining,
            category: cartonProduct.category,
            fabric: cartonProduct.fabric,
            composition: cartonProduct.composition,
            length: cartonProduct?.length,
            cartonId: cartonProduct.cartonId,
            quantityInCarton: cartonProduct.quantity,
            cartonProductId: cartonProduct.id,
            poNumber: cartonProduct?.purchaseOrder?.reference,
            weight: cartonProduct.weight,
            cubicVolume: cartonProduct.cubicVolume,
            hsCode: cartonProduct.hsCode,
            cfsCbm: cartonProduct?.cfsCbm,
            cfsWeight: cartonProduct?.cfsWeight,
            manufactureDetails: cartonProduct?.manufactureDetails,
            gender: cartonProduct?.gender,
            originCriterion: cartonProduct?.originCriterion,
            brandName:
              cartonProduct?.purchaseOrderProduct?.brandName || cartonProduct?.product?.brandName,
            purchaseOrderId: cartonProduct.purchaseOrderId,
            purchaseOrderProductId: cartonProduct.purchaseOrderProductId,
            unitOfMeasure: cartonProduct.unitOfMeasure,
          });
        }

        carton.products = products;
      }
    }
  }

  return cartonsView;
};

export const calculateScannedItemsFromBooking = (booking?: Booking) => {
  let total = 0;

  if (!booking || !booking.purchaseOrders || !booking.purchaseOrders.length) return total;

  for (const purchaseOrder of booking.purchaseOrders) {
    if (
      !purchaseOrder ||
      !purchaseOrder.purchaseOrderProducts ||
      !purchaseOrder.purchaseOrderProducts.length
    )
      continue;

    const { purchaseOrderProducts } = purchaseOrder;

    for (const purchaseOrderProduct of purchaseOrderProducts) {
      if (
        !purchaseOrderProduct ||
        !purchaseOrderProduct.cartonProducts ||
        !purchaseOrderProduct.cartonProducts.length
      )
        continue;

      const { cartonProducts } = purchaseOrderProduct;

      for (const cartonProduct of cartonProducts) {
        if (!cartonProduct || !cartonProduct.carton) continue;

        total += Number(cartonProduct.quantity || 0);
      }
    }
  }

  return total;
};

export const isBookingAvailableToGenerateDocument = (booking?: Booking) => {
  if (!booking || !booking.statusShipment) return false;

  return [
    BookingStatus.CONFIRMED,
    BookingStatus.PARTIALLY_DELIVERED,
    BookingStatus.DELIVERED,
    BookingStatus.UNDER_REVIEW,
    BookingStatus.CFS_RECEIVED,
    BookingStatus.CFS_REVIEW,
    BookingStatus.RECEIVED,
  ].includes(booking?.statusShipment as BookingStatus);
};

export const calculateBookingCartonTotals = (cartons: Carton[]) => {
  let bookedGrossWeight = 0;
  let bookedMeasurement = 0;
  let totalGrossWeight = 0;
  let totalMeasurement = 0;

  for (const carton of cartons) {
    bookedGrossWeight += Number(carton?.bookedGrossWeight || 0);
    bookedMeasurement += Number(carton?.bookedMeasurement || 0);
    totalGrossWeight += Number(carton?.grossWeight || 0);
    totalMeasurement += Number(carton?.measurement || 0);
  }

  return {
    bookedTotalQuantity: cartons?.length,
    bookedGrossWeight: Number(formatDecimalValue(DecimalFormatType.MAERSK, bookedGrossWeight)),
    bookedMeasurement: Number(formatDecimalValue(DecimalFormatType.MAERSK, bookedMeasurement)),
    totalGrossWeight: Number(formatDecimalValue(DecimalFormatType.MAERSK, totalGrossWeight)),
    totalMeasurement: Number(formatDecimalValue(DecimalFormatType.MAERSK, totalMeasurement)),
    cartons: convertCartonProductsToCartonsView({ cartons } as Booking),
  };
};

export const calculatePurchaseOrderTotal = (purchaseOrders: PurchaseOrder[], cartons: Carton[]) => {
  for (const purchaseOrder of purchaseOrders) {
    let totalQuantity: number = 0;

    const { purchaseOrderProducts } = purchaseOrder;

    const cartonsFromPurchaseOrder = cartons.filter(
      (carton) => carton.purchaseOrderId === purchaseOrder.id
    );

    const orderedQuantity =
      purchaseOrderProducts?.reduce((prev, curr) => (prev += Number(curr.quantity || 0)), 0) || 0;

    for (const carton of cartonsFromPurchaseOrder) {
      if (!carton?.cartonProducts?.length) continue;

      const cartonProductSum = carton.cartonProducts?.reduce(
        (prev, curr) => (prev += Number(curr?.quantity || 0)),
        0
      );

      totalQuantity += cartonProductSum;
    }

    const percentage = orderedQuantity ? (totalQuantity / orderedQuantity) * 100 : 0;

    purchaseOrder.orderedQuantity = orderedQuantity;
    purchaseOrder.percentage = percentage;
    purchaseOrder.totalQuantity = totalQuantity;
  }

  return purchaseOrders;
};
